import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet, Font } from '@react-pdf/renderer';

Font.register({
  family: 'Open Sans',
  fonts: [
    { src: '/fonts/OpenSans-Regular.ttf', fontWeight: 400 },
    { src: '/fonts/OpenSans-Medium.ttf', fontWeight: 500 },
    { src: '/fonts/OpenSans-SemiBold.ttf', fontWeight: 600 },
    { src: '/fonts/OpenSans-Bold.ttf', fontWeight: 700 },
  ],
});

// no hyphenation, it splits Croatian words in odd places
Font.registerHyphenationCallback((word) => [word]);

export type UpitnikListicA7Data = {
  key: string | number;
  groblje: string;
  oznaka: { label: string; value: string }[];
  qr: string; //  data URL
};

export type UpitnikListiciA7Texts = {
  naslov: string;
  opis: string;
  oznaka: string;
  komRedar: string;
  kontakt: string;
  upitnik: string;
  neUklanjati: string;
  potpis: string;
  url: string;
  test: string;
};

type UpitnikListiciA7PDFProps = {
  listici: UpitnikListicA7Data[];
  texts: UpitnikListiciA7Texts;
  grb: string | null; // PNG data URL
  probni?: boolean;
};

type ListicProps = Omit<UpitnikListiciA7PDFProps, 'listici'> & { l: UpitnikListicA7Data };

const MM = 72 / 25.4;
// A7 landscape, 105 x 74 mm
const PAGE_SIZE: [number, number] = [105 * MM, 74 * MM];
// most printers can't reach the paper edge, raise this if the border gets clipped
const BORDER_INSET = 2 * MM;
const FRAME_BORDER = 0.75;
const FRAME_PADDING = 1.5 * MM;
const QR_SIZE = 38 * MM;
// wider than the QR so a longer title like KOMUNALNI REDAR stays on one line
const RIGHT_WIDTH = 40 * MM;

const styles = StyleSheet.create({
  page: {
    padding: BORDER_INSET,
    fontFamily: 'Open Sans',
    fontSize: 11.5,
    color: '#000',
  },
  frame: {
    flexGrow: 1,
    borderWidth: FRAME_BORDER,
    borderColor: '#000',
    padding: FRAME_PADDING,
  },
  // both columns run to the bottom of the frame, their blocks spread apart so the slip is evenly filled
  top: {
    flexDirection: 'row',
    flexGrow: 1,
  },
  left: {
    flex: 1,
    paddingRight: 2 * MM,
  },
  // the oznaka box sits halfway between the description and the footer
  oznakaArea: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  naslov: {
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: 1.5,
  },
  grb: {
    width: 28,
    height: 28,
    objectFit: 'contain',
    marginLeft: 6,
  },
  opis: {
    marginTop: 4,
    // a unitless lineHeight scales the node's own fontSize, the inherited one doesn't count
    fontSize: 13.5,
    fontWeight: 500,
    // a hair tighter so the text breaks into 4 even lines
    letterSpacing: -0.45,
    lineHeight: 1.15,
    // textTransform: 'uppercase',
  },
  oznakaNaslov: {
    fontSize: 10,
  },
  oznakaBox: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    borderWidth: 0.75,
    borderColor: '#000',
    marginTop: 1,
    paddingVertical: 2,
  },
  oznakaCell: {
    alignItems: 'center',
  },
  oznakaLabel: {
    fontSize: 9.5,
    textTransform: 'uppercase',
  },
  oznakaValue: {
    fontSize: 18,
    fontWeight: 700,
    // room for descenders, a value like "yo" got its tail cut off
    lineHeight: 1.35,
  },
  right: {
    width: RIGHT_WIDTH,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  center: {
    alignItems: 'center',
  },
  komRedar: {
    fontSize: 11.5,
    fontWeight: 700,
    textAlign: 'center',
  },
  kontakt: {
    fontSize: 15.5,
    fontWeight: 700,
    textAlign: 'center',
    lineHeight: 1.15,
  },
  upitnik: {
    fontSize: 12,
    fontWeight: 500,
  },
  qr: {
    width: QR_SIZE,
    height: QR_SIZE,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginTop: 1.5 * MM,
  },
  potpis: {
    fontSize: 11,
    fontWeight: 500,
  },
  url: {
    fontSize: 8,
  },
  groblje: {
    fontSize: 12,
    fontWeight: 700,
    textAlign: 'left',
    marginTop: 0,
  },
  watermark: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  watermarkText: {
    fontSize: 90,
    fontWeight: 700,
    color: '#808080',
    opacity: 0.25,
    // along the A7 landscape diagonal
    transform: 'rotate(-35deg)',
  },
});

const Oznaka = ({ l }: { l: UpitnikListicA7Data; }) =>
  l.oznaka.length > 0 ? (
    <View>
      <Text style={styles.groblje}>{l.groblje}</Text>
      <View style={styles.oznakaBox}>
        {l.oznaka.map((o) => (
          <View key={o.label} style={styles.oznakaCell}>
            <Text style={styles.oznakaLabel}>{o.label}</Text>
            <Text style={styles.oznakaValue}>{o.value}</Text>
          </View>
        ))}
      </View>
    </View>
  ) : null;

const Listic = ({ l, texts, grb }: ListicProps) => (
  // wrap off here, not on Page: there it drops the page height and the page shrinks to the content
  <View style={styles.frame} wrap={false}>
    <View style={styles.top}>
      <View style={styles.left}>
        <View>
          <View style={styles.titleRow}>
            <Text style={styles.naslov}>{texts.naslov}</Text>
            {grb ? <Image src={grb} style={styles.grb} /> : null}
          </View>
          <Text style={styles.opis}>{texts.opis}</Text>
        </View>
        <View style={styles.oznakaArea}>
          <Oznaka l={l} />
        </View>
      </View>

      <View style={styles.right}>
        <View style={styles.center}>
          <Text style={styles.komRedar}>{texts.komRedar}</Text>
          <Text style={styles.kontakt}>{texts.kontakt}</Text>
        </View>
        <View style={styles.center}>
          <Text style={styles.upitnik}>{texts.upitnik}</Text>
          <Image src={l.qr} style={styles.qr} />
        </View>
      </View>
    </View>

    <View style={styles.footer}>
      <Text style={styles.potpis}>{texts.potpis}</Text>
      <Text style={styles.url}>{texts.url}</Text>
    </View>
  </View>
);

const UpitnikListiciA7PDF = ({ listici, probni, ...rest }: UpitnikListiciA7PDFProps) => (
  <Document title={rest.texts.naslov}>
    {listici.map((l) => (
      <Page key={l.key} size={PAGE_SIZE} style={styles.page}>
        <Listic l={l} {...rest} />
        {probni ? (
          <View style={styles.watermark} fixed>
            <Text style={styles.watermarkText}>{rest.texts.test}</Text>
          </View>
        ) : null}
      </Page>
    ))}
  </Document>
);

export default UpitnikListiciA7PDF;
